import React from "react";
import { useFela } from "react-fela";

import { useTheme } from "@/hooks/useTheme";
import { Grid } from "@/components/Grid";
import { Heading } from "@/components/Heading";
import { Image } from "@/components/Image";
import { Link } from "@/components/Link";
import { IUseCaseSteps } from "./types";
import { UseCaseHeaderSection } from "./UseCaseHeaderSection";

type IProps = IUseCaseSteps & JSX.IntrinsicElements["div"];

export const UseCaseSteps: React.FC<IProps> = ({
  title,
  steps,
  subtitle,
  hasUseCaseHeaderSection,
  ...rest
}) => {
  const { css } = useFela();
  const { bp, colors } = useTheme();
  return (
    <Grid
      id="UseCaseStepsSection"
      customStyle={{
        position: "relative",
        flexDirection: "column",
        alignItems: "center",
      }}
      {...rest}
    >
      {hasUseCaseHeaderSection ? (
        <UseCaseHeaderSection title={title} />
      ) : (
        <Heading
          as="h2"
          customStyle={{
            fontSize: 28,
            letterSpacing: "-0.01rem",
            [bp.FROM_DESKTOP]: {
              fontSize: 48,
              lineHeight: "60px",
            },
          }}
        >
          {title}
        </Heading>
      )}

      {subtitle && (
        <p
          className={css({
            fontSize: 18,
            lineHeight: "28px",
            letterSpacing: ".5px",
            maxWidth: 630,
            textAlign: "center",
            marginBottom: 75,
          })}
        >
          {subtitle}
        </p>
      )}
      <ol
        className={css({
          width: "100%",
          padding: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          listStyle: "none",
          [bp.FROM_DESKTOP]: {
            alignItems: "flex-start",
            marginTop: hasUseCaseHeaderSection ? 280 : 0,
          },
        })}
      >
        {steps.map(
          ({
            id,
            title,
            text,
            image,
            rightSidedImage,
            imageStyles,
            button,
          }) => (
            <li
              key={id}
              className={css({
                width: "100%",
                [bp.FROM_TABLET]: {
                  nested: {
                    ":not(:last-child) > div": {
                      paddingBottom: 160,
                    },
                  },
                },
              })}
            >
              <div
                className={css({
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "24px 0",
                  [bp.FROM_TABLET]: {
                    flexDirection: rightSidedImage ? "row" : "row-reverse",
                    padding: "24px 0",
                  },
                })}
              >
                <div
                  className={css({
                    flexShrink: 2,
                    marginBottom: 24,
                    [bp.FROM_TABLET]: {
                      maxWidth: 530,
                      marginBottom: 0,
                      paddingRight: rightSidedImage ? 20 : 0,
                      paddingLeft: rightSidedImage ? 0 : 20,
                    },
                  })}
                >
                  <Heading
                    as="h3"
                    customStyle={{
                      [bp.FROM_DESKTOP]: {
                        fontSize: 36,
                        lineHeight: "46px",
                        marginBottom: 24,
                      },
                    }}
                  >
                    {title}
                  </Heading>
                  <p
                    className={css({
                      color: colors.GRAY,
                    })}
                  >
                    {text}
                  </p>
                  {button && (
                    <Link
                      href={button.href}
                      customStyle={{
                        width: 148,
                        display: "block",
                        flexShrink: 0,
                        marginTop: 24,
                        textAlign: "center",
                        textDecoration: "none",
                        padding: "13px 0",
                        border: "none",
                        borderRadius: 8,
                        backgroundColor: colors.ORANGE,
                        fontSize: 14,
                        fontWeight: "bold",
                        textTransform: "uppercase",
                        color: colors.WHITE,
                      }}
                    >
                      {button.label}
                    </Link>
                  )}
                </div>

                <Image
                  customStyle={{
                    maxWidth: 470,
                    flexShrink: 1,
                    width: "100%",
                    margin: "0 auto",
                    filter: "drop-shadow(20px 20px 80px rgba(16, 5, 48, 0.08))",
                    ...imageStyles,
                  }}
                  src={image}
                  alt="Dashboard Screen"
                />
              </div>
            </li>
          ),
        )}
      </ol>
    </Grid>
  );
};
